import React from 'react';
import { Link } from 'react-router-dom';
import { Wrench, Fan, Phone, MapPin, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import useSEO from '../hooks/useSEO';

const Home = () => {
  const { t } = useLanguage();
  useSEO({
    title: 'PrimeTech Santorini | Επισκευές Επαγγελματικών Κουζινών στη Σαντορίνη',
    description: 'Άμεση τεχνική υποστήριξη για επαγγελματικές κουζίνες στη Σαντορίνη: φούρνοι, εστίες, φριτέζες, πλυντήρια πιάτων, απορροφητήρες, εξαερισμός. AI chat 24/7.',
  });

  const services = [
    {
      icon: <Wrench className="w-8 h-8" />,
      title: { el: 'Επισκευές Εξοπλισμού', en: 'Equipment Repairs' },
      desc: { el: 'Επισκευή ηλεκτρικών μηχανημάτων. Καθαρισμοί inverter και επισκευή φριτέζας.', en: 'Electric equipment repair. Inverter cleaning and fryer repair.' },
      color: 'bg-blue-50 text-blue-600',
      image: 'https://customer-assets.emergentagent.com/job_829760ef-d5ce-4dba-ba84-41f6905396d9/artifacts/tg8pas0i_IMG_0036.jpeg',
    },
    {
      icon: <Fan className="w-8 h-8" />,
      title: { el: 'Συστήματα Εξαερισμού', en: 'Ventilation Systems' },
      desc: { el: 'Εγκατάσταση και συντήρηση απορροφητήρων. Τοποθέτηση inverter βάση τα κυβικά του μοτέρ. Βάζουμε επιπλέον και φίλτρα άνθρακα αν χρειαστεί με στάδια για να βγαίνει άοσμο ο καπνός από την κουζίνα και πληρεί όλες τις προδιαγραφές.', en: 'Installation & maintenance of ventilation. Inverter installation based on motor capacity. We also install carbon filters with stages if needed for odor-free smoke extraction from the kitchen meeting all specifications.' },
      color: 'bg-cyan-50 text-cyan-600',
      image: 'https://customer-assets.emergentagent.com/job_829760ef-d5ce-4dba-ba84-41f6905396d9/artifacts/r3jgx38o_IMG_6422.heic',
    },
  ];

  const stats = [
    { value: '500+', label: { el: 'Επισκευές', en: 'Repairs' } },
    { value: '24/7', label: { el: 'Υποστήριξη', en: 'Support' } },
    { value: '10+', label: { el: 'Χρόνια Εμπειρίας', en: 'Years Experience' } },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center" 
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_e6cf0493-e8d0-4f07-a179-d2f65d4a4d25/artifacts/jq70vt7e_Generated_Image_May_20_2026_-_5_51PM.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-blue-950/90"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl animate-fade-in">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white mb-6 leading-tight" data-testid="hero-title">
              {t({
                el: 'ΤΕΧΝΙΚΗ ΥΠΟΣΤΗΡΙΞΗ ΕΠΑΓΓΕΛΜΑΤΙΚΩΝ ΚΟΥΖΙΝΩΝ',
                en: 'COMMERCIAL KITCHEN TECHNICAL SUPPORT'
              })}
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 mb-12 font-medium" data-testid="hero-subtitle">
              {t({
                el: 'Εξειδικευμένες υπηρεσίες για εστιατόρια και ξενοδοχεία στη Σαντορίνη',
                en: 'Specialized services for restaurants and hotels in Santorini'
              })}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/chat"
                data-testid="hero-report-issue-btn"
                className="bg-orange-500 text-white px-8 py-4 font-bold tracking-wider text-lg hover:bg-orange-600 transition-all inline-flex items-center justify-center space-x-2 pulse-slow border-glow"
              >
                <Phone className="w-6 h-6" />
                <span>{t({ el: 'ΑΝΑΦΟΡΑ ΒΛΑΒΗΣ', en: 'REPORT ISSUE' })}</span>
              </Link>
              
              <Link
                to="/services"
                data-testid="hero-services-btn"
                className="border-2 border-white text-white px-8 py-4 font-bold tracking-wider text-lg hover:bg-white hover:text-slate-900 transition-all inline-flex items-center justify-center"
              >
                {t({ el: 'ΟΙ ΥΠΗΡΕΣΙΕΣ ΜΑΣ', en: 'OUR SERVICES' })}
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-16 border-b border-slate-200" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-item-${index}`}>
                <div className="text-5xl font-extrabold text-slate-900 mb-2">{stat.value}</div>
                <div className="text-slate-600 font-semibold tracking-wider">{t(stat.label)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4" data-testid="services-title">
              {t({ el: 'ΟΙ ΥΠΗΡΕΣΙΕΣ ΜΑΣ', en: 'OUR SERVICES' })}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t({
                el: 'Ολοκληρωμένες λύσεις για την επαγγελματική σας κουζίνα',
                en: 'Complete solutions for your commercial kitchen'
              })}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {services.map((service, index) => (
              <Link
                key={index}
                to="/services"
                data-testid={`service-card-${index}`}
                className="bg-white border border-slate-200 overflow-hidden card-hover group block cursor-pointer"
              >
                {/* Image on top */}
                <div className="h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={t(service.title)}
                    className="w-full h-full object-cover transition-transform group-hover:scale-110"
                  />
                </div>
                
                {/* Content below */}
                <div className="p-6">
                  <div className={`w-12 h-12 ${service.color} rounded-lg flex items-center justify-center mb-4`}>
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-orange-500 transition-colors">{t(service.title)}</h3>
                  <p className="text-slate-600">{t(service.desc)}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="animate-slide-in">
              <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-8">
                {t({ el: 'ΓΙΑΤΙ PRIMETECH;', en: 'WHY PRIMETECH?' })}
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      {t({ el: 'Άμεση Ανταπόκριση', en: 'Quick Response' })}
                    </h3>
                    <p className="text-slate-600">
                      {t({
                        el: 'Κατανοούμε τη σημασία της συνεχούς λειτουργίας της κουζίνας σας',
                        en: 'We understand the importance of keeping your kitchen running'
                      })}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Wrench className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      {t({ el: 'Έμπειροι Τεχνικοί', en: 'Expert Technicians' })}
                    </h3>
                    <p className="text-slate-600">
                      {t({
                        el: 'Πιστοποιημένοι μάστορες με χρόνια εμπειρίας σε επαγγελματικά συστήματα',
                        en: 'Certified technicians with years of commercial system experience'
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      {t({ el: 'Τοπική Κάλυψη', en: 'Local Coverage' })}
                    </h3>
                    <p className="text-slate-600">
                      {t({
                        el: 'Εδρεύουμε στη Σαντορίνη και εξυπηρετούμε όλη τη Σαντορίνη',
                        en: 'Based in Santorini, serving all of Santorini'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_e6cf0493-e8d0-4f07-a179-d2f65d4a4d25/artifacts/i0f9befd_IMG_0902.png"
                alt="Επαγγελματική κουζίνα - σχέδιο"
                className="w-full h-[600px] object-cover shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent pointer-events-none"></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-24" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-6">
            {t({ el: 'ΧΡΕΙΑΖΕΣΤΕ ΤΕΧΝΙΚΗ ΥΠΟΣΤΗΡΙΞΗ;', en: 'NEED TECHNICAL SUPPORT?' })}
          </h2>
          <p className="text-xl text-slate-300 mb-12">
            {t({
              el: 'Το AI-powered σύστημά μας είναι εδώ να σας βοηθήσει 24/7',
              en: 'Our AI-powered system is here to help you 24/7'
            })}
          </p>
          <Link
            to="/chat"
            data-testid="cta-chat-btn"
            className="bg-orange-500 text-white px-12 py-5 text-lg font-bold tracking-wider hover:bg-orange-600 transition-all inline-flex items-center space-x-3 border-glow pulse-slow"
          >
            <Phone className="w-6 h-6" />
            <span>{t({ el: 'ΞΕΚΙΝΗΣΤΕ ΣΥΝΟΜΙΛΙΑ', en: 'START CHAT' })}</span>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
